import { sanityClient } from './client';
import { LISTING_CARD_PROJECTION } from './listings';
import type { SanityListingCard } from './listings';
import { DEFAULT_HOMEPAGE_TABS } from '@lib/homepage/tabs';
import type { HomepageTab, HomepageTabColour } from '@lib/homepage/tabs';

export const HOMEPAGE_SINGLETON_ID = 'homepage';
export const DEFAULT_LATEST_ARTICLES_HEADING = 'Our latest articles';
export const DEFAULT_HOMEPAGE_CARD_COUNT = 12;
const MIN_HOMEPAGE_CARD_COUNT = 1;

const TAB_COLOURS: HomepageTabColour[] = ['blue', 'magenta', 'green', 'amber'];
const DEFAULT_TAB_COLOUR: HomepageTabColour = 'blue';

export type RawHomepageTab = {
  _key: string;
  label?: string | null;
  cardLabel?: string | null;
  colour?: string | null;
  categorySlugs?: (string | null)[] | null;
  wordPressCategoryNames?: (string | null)[] | null;
  includesTeamReveals?: boolean | null;
  isCatchAll?: boolean | null;
};

export type RawHomepage = {
  latestArticlesHeading?: string | null;
  cardCount?: number | null;
  tabs?: RawHomepageTab[] | null;
  leadStory?: SanityListingCard | null;
};

export type HomepageSettings = {
  heading: string;
  cardCount: number;
  tabs: HomepageTab[];
  /** An editor-pinned lead story; the newest piece leads when unset. */
  leadStory?: SanityListingCard;
};

export const HOMEPAGE_SETTINGS_FALLBACK: HomepageSettings = {
  heading: DEFAULT_LATEST_ARTICLES_HEADING,
  cardCount: DEFAULT_HOMEPAGE_CARD_COUNT,
  tabs: DEFAULT_HOMEPAGE_TABS
};

export const homepageQuery = `
  *[_id == $id][0] {
    latestArticlesHeading,
    cardCount,
    tabs[] {
      _key,
      label,
      cardLabel,
      colour,
      "categorySlugs": categories[]->slug.current,
      wordPressCategoryNames,
      includesTeamReveals,
      isCatchAll
    },
    leadStory->${LISTING_CARD_PROJECTION}
  }
`;

const isTabColour = (value: unknown): value is HomepageTabColour =>
  TAB_COLOURS.includes(value as HomepageTabColour);

const compactStrings = (values?: (string | null)[] | null): string[] =>
  (values ?? []).filter((value): value is string => Boolean(value?.trim()));

const toHomepageTab = (raw: RawHomepageTab): HomepageTab | undefined => {
  const label = raw.label?.trim();
  if (!label) return undefined;
  return {
    id: raw._key,
    label,
    cardLabel: raw.cardLabel?.trim() || label,
    colour: isTabColour(raw.colour) ? raw.colour : DEFAULT_TAB_COLOUR,
    categorySlugs: compactStrings(raw.categorySlugs),
    wordPressCategoryNames: compactStrings(raw.wordPressCategoryNames),
    includesTeamReveals: Boolean(raw.includesTeamReveals),
    isCatchAll: Boolean(raw.isCatchAll)
  };
};

/**
 * A half-filled document still yields a working homepage: every field falls
 * back on its own, and an empty tab list means the default strands.
 */
export const buildHomepageSettings = (
  raw: RawHomepage | null | undefined
): HomepageSettings => {
  if (!raw) return HOMEPAGE_SETTINGS_FALLBACK;
  const tabs = (raw.tabs ?? [])
    .map(toHomepageTab)
    .filter((tab): tab is HomepageTab => Boolean(tab));
  const cardCount = Math.floor(raw.cardCount ?? 0);
  return {
    heading: raw.latestArticlesHeading?.trim() || DEFAULT_LATEST_ARTICLES_HEADING,
    cardCount:
      cardCount >= MIN_HOMEPAGE_CARD_COUNT ? cardCount : DEFAULT_HOMEPAGE_CARD_COUNT,
    tabs: tabs.length > 0 ? tabs : DEFAULT_HOMEPAGE_TABS,
    leadStory: raw.leadStory?.slug ? raw.leadStory : undefined
  };
};

export const getHomepageSettings = async (): Promise<HomepageSettings> => {
  try {
    const raw = await sanityClient.fetch<RawHomepage | null>(homepageQuery, {
      id: HOMEPAGE_SINGLETON_ID
    });
    return buildHomepageSettings(raw);
  } catch (error) {
    console.error(
      '[homepage] failed to fetch settings from Sanity, returning fallback',
      error
    );
    return HOMEPAGE_SETTINGS_FALLBACK;
  }
};
